import { useState, useEffect, type ReactNode } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  Heading,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Tabs,
  TabPanel,
} from '@aaimplatform/ui';
import { useSettings, useUpdateSettings } from '../hooks/useSettings';
import type {
  PlatformSettings,
  UpdatePlatformSettingsRequest,
} from '../api/services/platform-config';
import { ASSET_CLASS_LABELS } from '../generated/terminology-labels';
import { NOT_CONFIGURED } from './platform-config/SettingRows';

const SETTINGS_TABS_ID = 'platform-settings-tabs';

/**
 * The PlatformSettings fields this page edits and sends on save.
 *
 * Two declared fields are shown and never sent. enforceMfa: operators sign in
 * with Google Workspace SSO and Workspace is the second factor (operator
 * ruling 2026-08-24), so a platform switch claiming to enforce MFA stated a
 * posture the platform does not have. allowedCollateralTypes: the contract
 * does not bind its keys to AssetClass, the stored map carries keys of a
 * different vocabulary, and editing it from here wrote both vocabularies into
 * one record. A key the loaded document carries but the contract does not
 * declare is never sent either, because only the keys listed here are copied.
 */
export const EDITABLE_SETTINGS_KEYS = [
  'platformName',
  'supportEmail',
  'defaultTimezone',
  'dateFormat',
  'timeFormat',
  'sessionTimeout',
  'minPasswordLength',
  'requireSpecialChars',
  'requireNumbers',
  'ipAllowlistEnabled',
  'ipAllowlist',
  'apiRateLimit',
  'defaultLtvLimit',
  'collateralAlertThreshold',
  'autoCollateralAlert',
  'marketDataRefreshInterval',
  'priceStaleThreshold',
  'fallbackBehavior',
  'afterHoursPricing',
  'systemAlerts',
  'newTenantNotifications',
  'alertRecipients',
  'smsEnabled',
  'smsProvider',
  'webhookUrl',
  'webhookEnabled',
  'collateralDeficiencyWarningThreshold',
  'criticalLtvThreshold',
] as const satisfies readonly (keyof UpdatePlatformSettingsRequest)[];

/** The save body: the editable fields the form holds a value for, nothing else. */
export function toSettingsUpdate(form: PlatformSettings): UpdatePlatformSettingsRequest {
  const body: Record<string, unknown> = {};
  for (const key of EDITABLE_SETTINGS_KEYS) {
    if (form[key] !== undefined) body[key] = form[key];
  }
  return body as UpdatePlatformSettingsRequest;
}

/** The on-screen name of each field the page edits, for messages that list fields. */
export const SETTINGS_FIELD_LABELS: Record<(typeof EDITABLE_SETTINGS_KEYS)[number], string> = {
  platformName: 'Platform Name',
  supportEmail: 'Support Email',
  defaultTimezone: 'Default Timezone',
  dateFormat: 'Date Format',
  timeFormat: 'Time Format',
  sessionTimeout: 'Session timeout',
  minPasswordLength: 'Minimum password length',
  requireSpecialChars: 'Require special characters',
  requireNumbers: 'Require numbers',
  ipAllowlistEnabled: 'Enable IP allowlist',
  ipAllowlist: 'IP allowlist',
  apiRateLimit: 'Rate limit',
  defaultLtvLimit: 'Default LTV limit',
  collateralAlertThreshold: 'Collateral alert threshold',
  autoCollateralAlert: 'Automatic collateral alert notifications',
  marketDataRefreshInterval: 'Market data refresh interval',
  priceStaleThreshold: 'Price staleness threshold',
  fallbackBehavior: 'When the primary provider fails',
  afterHoursPricing: 'After-hours pricing rule',
  systemAlerts: 'System alerts',
  newTenantNotifications: 'New tenant notifications',
  alertRecipients: 'Alert recipients',
  smsEnabled: 'Enable SMS notifications',
  smsProvider: 'SMS provider',
  webhookUrl: 'Webhook URL',
  webhookEnabled: 'Enable webhooks',
  collateralDeficiencyWarningThreshold: 'Collateral deficiency warning threshold',
  criticalLtvThreshold: 'Critical LTV threshold',
};

/**
 * The editable fields the loaded document stores and the form has emptied.
 *
 * Saving cannot remove them. UpdatePlatformSettingsRequest declares no way to
 * say "remove this value" (no field is nullable), toSettingsUpdate leaves an
 * emptied field out of the body, and the service merges the body over the
 * stored document, so the stored value survives the save.
 */
export function clearedSettingsKeys(
  form: PlatformSettings,
  loaded: PlatformSettings,
): (typeof EDITABLE_SETTINGS_KEYS)[number][] {
  return EDITABLE_SETTINGS_KEYS.filter((key) => loaded[key] !== undefined && form[key] === undefined);
}

/**
 * Whether saving would change what is stored: some field the save body carries
 * differs from the stored value. An emptied field is not a change, since the
 * body omits it and the stored value stays, so it never turns Save on.
 */
export function settingsChanged(form: PlatformSettings, loaded: PlatformSettings): boolean {
  const body = toSettingsUpdate(form) as Record<string, unknown>;
  return Object.keys(body).some(
    (key) => JSON.stringify(body[key]) !== JSON.stringify((loaded as Record<string, unknown>)[key]),
  );
}

function numberOrUndefined(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

const TIME_FORMAT_OPTIONS = [
  { value: '12h', label: '12-hour (AM/PM)' },
  { value: '24h', label: '24-hour' },
];

const FALLBACK_OPTIONS = [
  { value: 'use_last_known', label: 'Use last known price' },
  { value: 'use_secondary', label: 'Switch to secondary provider' },
  { value: 'halt_operations', label: 'Halt operations' },
  { value: 'notify_only', label: 'Notify only' },
];

const AFTER_HOURS_OPTIONS = [
  { value: 'last_close', label: 'Use last close price' },
  { value: 'extended_hours', label: 'Use extended hours price' },
  { value: 'futures_implied', label: 'Use futures-implied price' },
  { value: 'disable_transactions', label: 'Disable transactions' },
];

const SMS_PROVIDER_OPTIONS = [
  { value: 'twilio', label: 'Twilio' },
  { value: 'aws_sns', label: 'AWS SNS' },
  { value: 'plivo', label: 'Plivo' },
];

function SectionHeading({ children, first = false }: { children: ReactNode; first?: boolean }) {
  return (
    <Heading level={2} as={3} size="card" className={first ? 'mb-4' : 'mt-8 mb-4'}>
      {children}
    </Heading>
  );
}

function NumberRow({
  id,
  label,
  description,
  unit,
  value,
  onChange,
  min,
  max,
}: {
  id: string;
  label: string;
  description?: string;
  unit?: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
}) {
  const descriptionId = description ? `${id}-description` : undefined;
  // The unit is part of the value's meaning, so it is announced with the
  // input rather than hidden from assistive technology.
  const unitId = unit ? `${id}-unit` : undefined;
  const describedBy = [descriptionId, unitId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-2 py-3 border-b border-recessed sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex-1">
        <label htmlFor={id} className="block text-sm font-sans font-medium text-primary">
          {label}
        </label>
        {description && (
          <p id={descriptionId} className="text-sm font-sans text-secondary">
            {description}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="w-40">
          <Input
            id={id}
            type="number"
            value={value ?? ''}
            placeholder={NOT_CONFIGURED}
            onChange={(e) => onChange(numberOrUndefined(e.target.value))}
            min={min}
            max={max}
            aria-describedby={describedBy}
            className="text-right font-mono tabular-nums"
          />
        </div>
        {unit && (
          <span id={unitId} className="text-sm text-secondary">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function SwitchRow({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="py-3 border-b border-recessed">
      <Switch
        label={label}
        description={value === undefined ? `${description} ${NOT_CONFIGURED}.` : description}
        isSelected={value === true}
        onChange={onChange}
      />
    </div>
  );
}

/**
 * The stored allowed-collateral map, read-only. Keys that are canonical
 * AssetClass members render through the terminology labels; keys outside that
 * vocabulary are counted rather than printed as raw codes.
 */
function AllowedCollateralTypes({ stored }: { stored: PlatformSettings['allowedCollateralTypes'] }) {
  if (!stored) {
    return <BodyText variant="ui" size="sm" color="secondary">{NOT_CONFIGURED}</BodyText>;
  }
  const entries = Object.entries(stored);
  const canonical = entries.filter(([key]) => key in ASSET_CLASS_LABELS);
  const outside = entries.length - canonical.length;
  return (
    <div className="space-y-3">
      <BodyText variant="ui" size="sm" color="secondary">
        Read-only here. The settings contract does not bind this map to the
        asset-class vocabulary, so it cannot be edited from this page without
        mixing two vocabularies in one record.
      </BodyText>
      {canonical.length > 0 && (
        <dl className="divide-y divide-recessed">
          {canonical.map(([key, allowed]) => (
            <div key={key} className="flex justify-between gap-4 py-2 text-sm font-sans">
              <dt className="text-primary">{ASSET_CLASS_LABELS[key]}</dt>
              <dd className="text-secondary">{allowed ? 'Allowed' : 'Not allowed'}</dd>
            </div>
          ))}
        </dl>
      )}
      {outside > 0 && (
        <BodyText variant="ui" size="sm" color="secondary">
          {outside === 1
            ? '1 stored entry uses a key outside the canonical asset-class vocabulary and is not shown.'
            : `${outside} stored entries use keys outside the canonical asset-class vocabulary and are not shown.`}
        </BodyText>
      )}
    </div>
  );
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved] = useState(false);
  const [newIp, setNewIp] = useState('');

  const { data: loadedSettings, isLoading, error } = useSettings();
  const updateSettings = useUpdateSettings();

  // The edits hold only what the server returned plus the operator's changes.
  // Until the read succeeds there is no document and nothing renders in its
  // place; a field the document lacks stays undefined and reads as Not
  // configured. A new document from the server (after a save) replaces them.
  const [edits, setEdits] = useState<PlatformSettings | null>(null);

  useEffect(() => {
    setEdits(null);
  }, [loadedSettings]);

  const form: PlatformSettings | null = edits ?? loadedSettings ?? null;

  const set = <K extends keyof PlatformSettings>(key: K, value: PlatformSettings[K]) => {
    setSaved(false);
    setEdits((prev) => {
      const base = prev ?? loadedSettings;
      return base ? { ...base, [key]: value } : prev;
    });
  };

  const isDirty = form !== null && loadedSettings !== undefined && settingsChanged(form, loadedSettings);
  const cleared =
    form !== null && loadedSettings !== undefined ? clearedSettingsKeys(form, loadedSettings) : [];

  const handleSave = async () => {
    if (!form) return;
    try {
      await updateSettings.mutateAsync(toSettingsUpdate(form));
      // Stays until the next edit, in a live region, so it is announced and
      // is not gone before it can be read.
      setSaved(true);
    } catch {
      // Error is available via updateSettings.error
    }
  };

  const handleReset = () => {
    setSaved(false);
    setEdits(null);
  };

  const ipAllowlist = form?.ipAllowlist ?? [];

  const handleAddIp = () => {
    if (newIp && !ipAllowlist.includes(newIp)) {
      set('ipAllowlist', [...ipAllowlist, newIp]);
      setNewIp('');
    }
  };

  const handleRemoveIp = (ip: string) => {
    set('ipAllowlist', ipAllowlist.filter((i) => i !== ip));
  };

  // Lane B1 (sprint-portal-uxia-audit, 2026-05-17): renamed
  // "Default Loan Settings" -> "Collateral Settings". The tab configures
  // LTV limits, alert thresholds, automation, and allowed collateral asset
  // types -- the platform's collateral / capacity configuration surface,
  // not loan-origination configuration.
  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'security', label: 'Security' },
    { id: 'loans', label: 'Collateral Settings' },
    { id: 'market', label: 'Market Data' },
    { id: 'notifications', label: 'Notifications' },
  ];

  if (isLoading) {
    return (
      <div aria-busy="true">
        <div className="flex justify-end gap-3 mb-6">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-36" />
        </div>
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <Alert type="error" title="Settings could not be loaded">
        {error ? error.message : 'The settings read returned no document.'} Nothing
        is shown in its place, and saving is unavailable until the read succeeds.
      </Alert>
    );
  }

  return (
    <div>
      <PageHeader
        description="Platform-wide settings stored in the platform settings record."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" onClick={handleReset} disabled={!isDirty && cleared.length === 0}>
              Reset
            </Button>
            <Button onClick={() => void handleSave()} disabled={!isDirty || updateSettings.isPending}>
              {updateSettings.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        }
      />

      <Alert type="warning" title="Not enforced" className="mb-6">
        No platform service reads these settings yet. Saving stores them in the
        platform settings record; it does not change sign-in, password rules, IP
        access, rate limits, market data handling or notification delivery.
      </Alert>

      {cleared.length > 0 && (
        <Alert type="warning" title="Not cleared" className="mb-6">
          {cleared.map((key) => SETTINGS_FIELD_LABELS[key]).join(', ')}{' '}
          {cleared.length === 1 ? 'keeps its' : 'keep their'} stored{' '}
          {cleared.length === 1 ? 'value' : 'values'}: the settings contract has no
          way to remove a stored value, so saving does not clear{' '}
          {cleared.length === 1 ? 'it' : 'them'}. Reset restores the stored{' '}
          {cleared.length === 1 ? 'value' : 'values'} on this form.
        </Alert>
      )}

      {/* Save results: live regions present before a result is inserted. */}
      <div role="status" aria-live="polite">
        {saved && (
          <Alert type="success" className="mb-4">
            Settings saved.
          </Alert>
        )}
      </div>
      <div role="alert">
        {updateSettings.error && (
          <Alert type="error" className="mb-4">
            Failed to save settings: {updateSettings.error.message}
          </Alert>
        )}
      </div>

      {/* The tab strip sits on the canvas, as on every other tabbed operator
          page (Tenant Detail, ATTOM, UCC Entities); the card holds the form. */}
      <Tabs
        id={SETTINGS_TABS_ID}
        aria-label="Settings sections"
        externalPanels
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <Card className="mt-6">
        {/* General Settings Tab */}
        <TabPanel tabsId={SETTINGS_TABS_ID} tabId="general" activeTab={activeTab}>
          <SectionHeading first>Identity and locale</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              id="settings-platform-name"
              label="Platform Name"
              value={form.platformName ?? ''}
              placeholder={NOT_CONFIGURED}
              onChange={(e) => set('platformName', e.target.value)}
            />
            <Input
              id="settings-support-email"
              label="Support Email"
              type="email"
              value={form.supportEmail ?? ''}
              placeholder={NOT_CONFIGURED}
              onChange={(e) => set('supportEmail', e.target.value)}
            />
            <Select
              id="settings-default-timezone"
              label="Default Timezone"
              options={TIMEZONE_OPTIONS}
              placeholder={NOT_CONFIGURED}
              value={form.defaultTimezone ?? ''}
              onChange={(e) => set('defaultTimezone', e.target.value)}
            />
            <Select
              id="settings-date-format"
              label="Date Format"
              options={DATE_FORMAT_OPTIONS}
              placeholder={NOT_CONFIGURED}
              value={form.dateFormat ?? ''}
              onChange={(e) => set('dateFormat', e.target.value)}
            />
            <Select
              id="settings-time-format"
              label="Time Format"
              options={TIME_FORMAT_OPTIONS}
              placeholder={NOT_CONFIGURED}
              value={form.timeFormat ?? ''}
              onChange={(e) => set('timeFormat', e.target.value)}
            />
          </div>
        </TabPanel>

        {/* Security Settings Tab */}
        <TabPanel tabsId={SETTINGS_TABS_ID} tabId="security" activeTab={activeTab}>
          <SectionHeading first>Authentication</SectionHeading>
          <NumberRow
            id="settings-session-timeout"
            label="Session timeout"
            description="Auto-logout after inactivity"
            unit="min"
            value={form.sessionTimeout}
            onChange={(v) => set('sessionTimeout', v)}
          />
          <div className="py-3 border-b border-recessed">
            <BodyText variant="ui" size="sm" className="font-medium text-primary">
              Second factor
            </BodyText>
            <BodyText variant="ui" size="sm" color="secondary">
              Operators sign in with Google Workspace SSO, and Workspace provides
              the second factor. Elsewhere a second factor is set per identity:
              members opt in on their profile, and FI staff follow their
              institution&apos;s identity provider.
            </BodyText>
          </div>

          <SectionHeading>Password Policy</SectionHeading>
          <NumberRow
            id="settings-min-password-length"
            label="Minimum password length"
            unit="characters"
            value={form.minPasswordLength}
            onChange={(v) => set('minPasswordLength', v)}
            min={8}
            max={32}
          />
          <SwitchRow
            label="Require special characters"
            description="At least one of !@#$%^&*()_+-="
            value={form.requireSpecialChars}
            onChange={(v) => set('requireSpecialChars', v)}
          />
          <SwitchRow
            label="Require numbers"
            description="At least one numeric character"
            value={form.requireNumbers}
            onChange={(v) => set('requireNumbers', v)}
          />

          <SectionHeading>IP Allowlist</SectionHeading>
          <SwitchRow
            label="Enable IP allowlist"
            description="Restrict platform admin access to specific IP addresses or ranges"
            value={form.ipAllowlistEnabled}
            onChange={(v) => set('ipAllowlistEnabled', v)}
          />
          {form.ipAllowlistEnabled === true && (
            <div className="mt-4">
              <div className="flex gap-2 mb-4">
                <Input
                  id="settings-new-ip"
                  aria-label="IP address or CIDR range to add"
                  placeholder="IP address or CIDR (e.g., 10.0.0.0/8)"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleAddIp} variant="secondary">
                  Add
                </Button>
              </div>
              {ipAllowlist.length === 0 ? (
                <BodyText variant="ui" size="sm" color="secondary">
                  No addresses stored.
                </BodyText>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {ipAllowlist.map((ip) => (
                    <li
                      key={ip}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-elevated rounded-full text-sm"
                    >
                      <span>{ip}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveIp(ip)}
                        aria-label={`Remove ${ip}`}
                        className="text-secondary hover:text-error"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <SectionHeading>API Rate Limiting</SectionHeading>
          <NumberRow
            id="settings-api-rate-limit"
            label="Rate limit"
            description="Maximum API requests per minute per client"
            unit="/min"
            value={form.apiRateLimit}
            onChange={(v) => set('apiRateLimit', v)}
          />
        </TabPanel>

        {/* Collateral Settings Tab */}
        <TabPanel tabsId={SETTINGS_TABS_ID} tabId="loans" activeTab={activeTab}>
          <SectionHeading first>Loan-to-Value Settings</SectionHeading>
          <NumberRow
            id="settings-default-ltv-limit"
            label="Default LTV limit"
            unit="%"
            value={form.defaultLtvLimit}
            onChange={(v) => set('defaultLtvLimit', v)}
            min={0}
            max={100}
          />
          <NumberRow
            id="settings-collateral-alert-threshold"
            label="Collateral alert threshold"
            description="LTV that triggers a collateral deficiency alert"
            unit="%"
            value={form.collateralAlertThreshold}
            onChange={(v) => set('collateralAlertThreshold', v)}
            min={0}
            max={100}
          />

          <SectionHeading>Collateral Alert Automation</SectionHeading>
          <SwitchRow
            label="Automatic collateral alert notifications"
            description="Send notifications when LTV exceeds the threshold"
            value={form.autoCollateralAlert}
            onChange={(v) => set('autoCollateralAlert', v)}
          />

          <SectionHeading>Allowed Collateral Types</SectionHeading>
          <AllowedCollateralTypes stored={form.allowedCollateralTypes} />
        </TabPanel>

        {/* Market Data Settings Tab */}
        <TabPanel tabsId={SETTINGS_TABS_ID} tabId="market" activeTab={activeTab}>
          <SectionHeading first>Data Refresh Settings</SectionHeading>
          <NumberRow
            id="settings-market-data-refresh"
            label="Market data refresh interval"
            description="How often to fetch market data updates"
            unit="min"
            value={form.marketDataRefreshInterval}
            onChange={(v) => set('marketDataRefreshInterval', v)}
          />
          <NumberRow
            id="settings-price-stale-threshold"
            label="Price staleness threshold"
            description="Mark prices as stale after this duration"
            unit="min"
            value={form.priceStaleThreshold}
            onChange={(v) => set('priceStaleThreshold', v)}
          />

          <SectionHeading>Fallback Behavior</SectionHeading>
          <Select
            id="settings-fallback-behavior"
            label="When the primary provider fails"
            options={FALLBACK_OPTIONS}
            placeholder={NOT_CONFIGURED}
            value={form.fallbackBehavior ?? ''}
            onChange={(e) => set('fallbackBehavior', e.target.value)}
          />

          <SectionHeading>After-Hours Pricing</SectionHeading>
          <Select
            id="settings-after-hours-pricing"
            label="After-hours pricing rule"
            options={AFTER_HOURS_OPTIONS}
            placeholder={NOT_CONFIGURED}
            value={form.afterHoursPricing ?? ''}
            onChange={(e) => set('afterHoursPricing', e.target.value)}
          />
        </TabPanel>

        {/* Notification Settings Tab */}
        <TabPanel tabsId={SETTINGS_TABS_ID} tabId="notifications" activeTab={activeTab}>
          <SectionHeading first>Email Notifications</SectionHeading>
          <SwitchRow
            label="System alerts"
            description="Email notifications for system issues"
            value={form.systemAlerts}
            onChange={(v) => set('systemAlerts', v)}
          />
          <SwitchRow
            label="New tenant notifications"
            description="Alert when new tenants onboard"
            value={form.newTenantNotifications}
            onChange={(v) => set('newTenantNotifications', v)}
          />
          <div className="mt-4">
            <Input
              id="settings-alert-recipients"
              label="Alert recipients (comma-separated)"
              value={form.alertRecipients ?? ''}
              placeholder={NOT_CONFIGURED}
              onChange={(e) => set('alertRecipients', e.target.value)}
            />
          </div>

          <SectionHeading>SMS Settings</SectionHeading>
          <SwitchRow
            label="Enable SMS notifications"
            description="Send critical alerts by SMS"
            value={form.smsEnabled}
            onChange={(v) => set('smsEnabled', v)}
          />
          {form.smsEnabled === true && (
            <div className="mt-4">
              <Select
                id="settings-sms-provider"
                label="SMS provider"
                options={SMS_PROVIDER_OPTIONS}
                placeholder={NOT_CONFIGURED}
                value={form.smsProvider ?? ''}
                onChange={(e) => set('smsProvider', e.target.value)}
              />
            </div>
          )}

          <SectionHeading>Webhook Configuration</SectionHeading>
          <SwitchRow
            label="Enable webhooks"
            description="Send event notifications to external systems"
            value={form.webhookEnabled}
            onChange={(v) => set('webhookEnabled', v)}
          />
          {form.webhookEnabled === true && (
            <div className="mt-4">
              <Input
                id="settings-webhook-url"
                label="Webhook URL"
                type="url"
                value={form.webhookUrl ?? ''}
                placeholder="https://example.com/webhook"
                onChange={(e) => set('webhookUrl', e.target.value)}
              />
            </div>
          )}

          <SectionHeading>Alert Thresholds</SectionHeading>
          <NumberRow
            id="settings-deficiency-warning-threshold"
            label="Collateral deficiency warning threshold"
            description="LTV that triggers a deficiency warning"
            unit="%"
            value={form.collateralDeficiencyWarningThreshold}
            onChange={(v) => set('collateralDeficiencyWarningThreshold', v)}
            min={0}
            max={100}
          />
          <NumberRow
            id="settings-critical-ltv-threshold"
            label="Critical LTV threshold"
            description="LTV that triggers a critical alert"
            unit="%"
            value={form.criticalLtvThreshold}
            onChange={(v) => set('criticalLtvThreshold', v)}
            min={0}
            max={100}
          />
        </TabPanel>
      </Card>
    </div>
  );
}
